import Link from 'next/link'
import styled, { css } from 'styled-components'
import { Achievement, Person } from './api'
import { ACH_ICON, Icon, SectionTitle, Surface } from './Identity'
import { InlineTicks, ticksLabel } from './PersonTicks'

import { AvatarImg, short } from './avatar'

export { AvatarImg, GLOW, short } from './avatar'

export const Page = styled.main`
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  padding: 20px 16px 120px;
  display: grid;
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 32px 24px 80px;
    grid-template-columns: minmax(0, 1fr) 360px;
    align-items: start;
  }
`
export const Col = styled.div`
  display: grid;
  gap: 16px;
  min-width: 0;
`
export const Card = styled.section<{ $hero?: boolean }>`
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 24px;
  padding: 20px;
  min-width: 0;
  ${({ $hero }) =>
    $hero &&
    css`
      background: radial-gradient(120% 90% at 0% 0%, rgba(127, 212, 232, 0.18), transparent 60%),
        radial-gradient(90% 80% at 100% 0%, rgba(118, 69, 217, 0.22), transparent 60%),
        ${({ theme }) => theme.colors.backgroundAlt};
      box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
    `}
`
export const H2 = styled.h2`
  font-size: 20px;
  line-height: 1.3;
  font-weight: 700;
  margin: 0 0 12px;
  color: ${({ theme }) => theme.colors.text};
`
export const Muted = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
`
export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
`
export const Btn = styled.button<{ $ghost?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  min-height: 44px;
  padding: 0 20px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid ${({ $ghost }) => ($ghost ? 'rgba(127, 212, 232, 0.5)' : 'transparent')};
  color: ${({ $ghost, theme }) => ($ghost ? theme.colors.text : '#0b1220')};
  background: ${({ $ghost }) => ($ghost ? 'transparent' : 'linear-gradient(135deg, #7fd4e8, #9a7bff)')};
  box-shadow: ${({ $ghost }) => ($ghost ? 'none' : '0 6px 22px rgba(127, 212, 232, 0.35)')};
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`
export const Tag = styled.span<{ $tone?: 'ok' | 'warn' | 'off' }>`
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $tone }) => ($tone === 'ok' ? '#8ff0c4' : $tone === 'warn' ? '#ffd27a' : '#c9c3dd')};
  background: ${({ $tone }) => ($tone === 'ok' ? 'rgba(49, 208, 170, 0.14)' : $tone === 'warn' ? 'rgba(255, 178, 55, 0.14)' : 'rgba(255, 255, 255, 0.08)')};
`

const PersonRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 6px 4px;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`
export function PersonItem({ p }: { p: Person }) {
  const off = p.state === 'off'
  const name = off ? short(p.address) : p.handle ? `@${p.handle}` : short(p.address)
  const ticks = off ? '' : ticksLabel(p.official, p.teams)
  return (
    <PersonRow href={`/u/${p.address}`} aria-label={ticks ? `${name}, ${ticks}` : undefined} data-testid="person-row">
      <AvatarImg avatar={p.avatar} size={40} off={off} />
      <span style={{ minWidth: 0, display: 'grid' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <strong style={{ fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{name}</strong>
          {!off && <InlineTicks official={p.official} teams={p.teams} size={20} />}
        </span>
        <span style={{ fontSize: 13, opacity: 0.7 }}>{off ? 'Profile off' : p.handle ? short(p.address) : 'No handle yet'}</span>
      </span>
    </PersonRow>
  )
}

const Grid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  }
`
const Badge = styled.li<{ $on: boolean }>`
  position: relative;
  border-radius: 20px;
  padding: 14px 14px 16px;
  min-height: 150px;
  display: grid;
  align-content: start;
  gap: 10px;
  border: 1px solid ${({ $on }) => ($on ? 'rgba(127, 212, 232, 0.4)' : 'rgba(255, 255, 255, 0.07)')};
  background: ${({ $on }) => ($on ? 'radial-gradient(120% 90% at 20% 0%, rgba(127, 212, 232, 0.22), transparent 60%), linear-gradient(160deg, rgba(154, 123, 255, 0.18), rgba(13, 21, 48, 0.6))' : 'rgba(255, 255, 255, 0.025)')};
  box-shadow: ${({ $on }) => ($on ? '0 14px 30px -16px rgba(127, 212, 232, 0.5)' : 'none')};
  .ring {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: grid;
    place-items: center;
    color: ${({ $on }) => ($on ? '#0b1220' : 'rgba(201,195,221,0.55)')};
    background: ${({ $on }) => ($on ? 'linear-gradient(135deg, #b9f0ff, #7fd4e8 45%, #b38bff)' : 'rgba(255,255,255,0.05)')};
    box-shadow: ${({ $on }) => ($on ? '0 8px 20px -6px rgba(127, 212, 232, 0.75), 0 1px 0 rgba(255,255,255,0.4) inset' : 'none')};
  }
  strong {
    display: block;
    font-size: 15px;
    line-height: 1.25;
    color: ${({ theme, $on }) => ($on ? theme.colors.text : theme.colors.textSubtle)};
  }
  span {
    font-size: 13px;
    line-height: 1.4;
    color: ${({ theme }) => theme.colors.textSubtle};
    opacity: ${({ $on }) => ($on ? 1 : 0.75)};
  }
  .lock {
    position: absolute;
    top: 12px;
    right: 12px;
    color: rgba(201, 195, 221, 0.45);
  }
`
const Progress = styled.div`
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  overflow: hidden;
  margin: 12px 0 14px;
  i {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #7fd4e8, #9a7bff);
    box-shadow: 0 0 14px rgba(127, 212, 232, 0.6);
    transition: width 400ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
`
export function Achievements({ items }: { items: Achievement[] }) {
  const earned = items.filter((a) => a.earned).length
  return (
    <Surface data-testid="achievements">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <SectionTitle as="h2">Achievements</SectionTitle>
        <strong style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
          {earned} <span style={{ opacity: 0.6, fontWeight: 600 }}>of {items.length}</span>
        </strong>
      </div>
      <Progress aria-hidden>
        <i style={{ width: `${items.length ? (earned / items.length) * 100 : 0}%` }} />
      </Progress>
      <Grid>
        {items.map((a) => (
          <Badge key={a.id} $on={a.earned} data-testid={`ach-${a.id}`} data-earned={a.earned}>
            <span className="ring">
              <Icon name={ACH_ICON[a.id] ?? 'badge'} size={26} />
            </span>
            {!a.earned && (
              <span className="lock" aria-label="Locked">
                <Icon name="lock" size={16} />
              </span>
            )}
            <strong>{a.title}</strong>
            <span>{a.description}</span>
          </Badge>
        ))}
      </Grid>
    </Surface>
  )
}
